import { BehaviorSubject, firstValueFrom, Observable, shareReplay } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { CftAssessment } from '../models/cool-farm-tool-assessment.model';
import { CftConnection, MappingData } from '../../cft/create-assessment/types';

@Injectable()
export class CftService {
  private endpoint = environment.apiBaseUrl;
  private pathCftLivestock = 'api/private/v1/livestock/cft';
  private pathCft = 'api/private/v1/cft';

  // Keeps track of:

  // All dairy assessments
  $dairyAssessments: BehaviorSubject<CftAssessment[]> = new BehaviorSubject<CftAssessment[]>([]);

  // Details of the CftAssessment which the user is looking at
  $dairyAssessment: BehaviorSubject<CftAssessment> = new BehaviorSubject<CftAssessment>(null);

  // Mapping of the translated options for the assessment
  mapping: Observable<MappingData> = new Observable<MappingData>(null);

  http = inject(HttpClient);

  constructor() {
    this.loadMapping();
  }

  /**
   * @description: Get CFT connection
   */
  async getConnection() {
    return firstValueFrom(
      this.http.get<CftConnection>(`${this.endpoint}${this.pathCft}/connection`)
    );
  }

  /**
   * @description: Connect to CFT
   * @param data apiKey and apiAppKey connection strings
   */
  async connect(data: { apiKey: string; apiAppKey: string }) {
    return firstValueFrom(
      this.http.post<CftConnection>(`${this.endpoint}${this.pathCft}/connect`, data)
    );
  }

  /**
   * @description: Disconnect from CFT
   */
  async disconnect() {
    return firstValueFrom(
      this.http.delete<CftConnection>(`${this.endpoint}${this.pathCft}/disconnect`)
    );
  }

  /**
   * @description: Load CFT mapping items
   */
  async loadMapping() {
    // Load the mapping data only one time
    this.mapping = this.http
      .get<MappingData>(`${this.endpoint}${this.pathCft}/mapping`)
      .pipe(shareReplay(1));
  }

  /**
   * @description: Get the CFT farm list
   * @returns {Promise<>}
   */
  async getFarms(): Promise<any> {
    return this.http
      .get<CftAssessment>(`${this.endpoint}${this.pathCft}/farms?all=true`)
      .toPromise();
  }

  /**
   * @description: Get all assessments
   * @returns {Promise<CftAssessment[]>}
   */
  async getCftAssessments(all: boolean = false): Promise<CftAssessment[]> {
    const response = await firstValueFrom(
      this.http.get<CftAssessment[]>(`${this.endpoint}${this.pathCftLivestock}?all=${all}`)
    );
    this.$dairyAssessments.next(response);
    return response;
  }

  /**
   * @description: Get the whole object of a specific assessment by ID
   * @param id {string} the id of the path
   * @returns {Promise<CftAssessment>}
   */
  async getAssessmentById(id: number): Promise<CftAssessment> {
    const response = await firstValueFrom(
      this.http.get<CftAssessment>(`${this.endpoint}${this.pathCftLivestock}/${id}?all=true`)
    );
    this.$dairyAssessment.next(response);
    return response;
  }

  /**
   * @description: Create a new assessment
   * @returns {Promise<CftAssessment>}
   * @param year {number} year related to the assessment
   * @param slug {string} cft farm slug related to the assessment
   * @param companyId {number} id of the xfarm company to assign the assessment
   */
  async createAssessment({
    year,
    slug,
    companyId,
  }: {
    year: number;
    slug: string;
    companyId: number;
  }): Promise<CftAssessment> {
    return firstValueFrom(
      this.http.post<CftAssessment>(`${this.endpoint}${this.pathCftLivestock}?all=true`, {
        year,
        slug,
        companyId,
      })
    );
  }

  /**
   * @description: Update an existing assessment
   * @returns {Promise<CftAssessment>}
   * @param assessment
   */
  async updateAssessment(assessment: CftAssessment): Promise<CftAssessment> {
    return firstValueFrom(
      this.http.put<CftAssessment>(
        `${this.endpoint}${this.pathCftLivestock}/${assessment.id}?all=true`,
        assessment
      )
    );
  }

  /**
   * @description: Delete a assessment by ID
   * @returns {Promise<void>} - Promise resolving to void, otherwise throws error
   * @param id
   */
  async deleteAssessment(id: number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.endpoint}${this.pathCftLivestock}/${id}?all=true`)
    );
  }

  /**
   * @description: Delete a assessment by ID
   * @returns {Promise<void>} - Promise resolving to void, otherwise throws error
   * @param id
   */
  async sendAssessment(id: number): Promise<void> {
    await firstValueFrom(
      this.http.post(`${this.endpoint}${this.pathCftLivestock}/${id}?all=true`, {})
    );
  }
}
